import api from './api';

export interface Creneau {
  id: number;
  service_id: number;
  creneau_date: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
  is_booked: boolean;
  trainee_id?: number;
  notes?: string;
  booking_status?: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  service: {
    id: number;
    title: string;
    banner_image?: string;
    country?: string;
    city?: string;
    format?: string;
    estimated_duration?: string;
    languages?: string;
    full_description?: string;
    domaine?: {
      id: number;
      name_french: string;
      name_english: string;
    };
    subcategory?: {
      id: number;
      name_french: string;
      name_english: string;
    };
      training_center: {
        id: number;
        name: string;
        logo?: string;
      };
    };
    trainee?: {
      id: number;
      firstname: string;
      lastname: string;
      email: string;
      phone?: string;
    };
}

export interface CreateCreneauData {
  service_id: number;
  creneau_date: string;
  start_time: string;
  end_time: string;
  is_available?: boolean;
  notes?: string;
}

export interface UpdateCreneauData {
  start_time?: string;
  end_time?: string;
  is_available?: boolean;
  is_booked?: boolean;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  notes?: string;
}

class CreneauxService {
  async getAllCreneaux(): Promise<Creneau[]> {
    const response = await api.get('/public/creneaux');
    return response.data;
  }

  async getCreneauxByConsultant(consultantId: number): Promise<Creneau[]> {
    const response = await api.get(`/creneaux/consultant/${consultantId}`);
    return response.data;
  }

  async getCreneauxByService(serviceId: string): Promise<Creneau[]> {
    const response = await api.get(`/public/creneaux/available?serviceId=${serviceId}`);
    return response.data;
  }

  async getAllCreneauxByService(serviceId: string): Promise<Creneau[]> {
    const response = await api.get(`/public/creneaux/service/${serviceId}`);
    return response.data;
  }

  async getCreneauxByDateRange(startDate: string, endDate: string): Promise<Creneau[]> {
    const response = await api.get(`/creneaux/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async getCreneau(id: number): Promise<Creneau> {
    const response = await api.get(`/creneaux/${id}`);
    return response.data;
  }

  async createCreneau(data: CreateCreneauData): Promise<Creneau> {
    const response = await api.post('/creneaux', data);
    return response.data;
  }

  async updateCreneau(id: number, data: UpdateCreneauData): Promise<Creneau> {
    const response = await api.patch(`/creneaux/${id}`, data);
    return response.data;
  }

  async deleteCreneau(id: number): Promise<void> {
    await api.delete(`/creneaux/${id}`);
  }

  async bookCreneau(id: number, bookingData: {
    trainee_id: number;
    notes?: string;
  }): Promise<Creneau> {
    const response = await api.patch(`/creneaux/${id}/book`, bookingData);
    return response.data;
  }

  async bookCreneauForCurrentUser(id: number, notes?: string): Promise<Creneau> {
    const response = await api.post(`/creneaux/${id}/book`, { notes });
    return response.data;
  }

  async getMyTraineeCreneaux(): Promise<Creneau[]> {
    const response = await api.get('/creneaux/my/trainee');
    return response.data;
  }

  async getMyConsultantCreneaux(): Promise<Creneau[]> {
    const response = await api.get('/creneaux/my/consultant');
    return response.data;
  }

  async getMyPendingBookings(): Promise<Creneau[]> {
    const response = await api.get('/creneaux/my/consultant/pending');
    return response.data;
  }

  async getAllMyBookings(): Promise<Creneau[]> {
    const response = await api.get('/creneaux/my/consultant/all');
    return response.data;
  }

  async approveBooking(id: number): Promise<Creneau> {
    const response = await api.post(`/creneaux/${id}/approve`);
    return response.data;
  }

  async rejectBooking(id: number, reason?: string): Promise<Creneau> {
    const response = await api.post(`/creneaux/${id}/reject`, { reason });
    return response.data;
  }

  async cancelBooking(id: number): Promise<Creneau> {
    const response = await api.patch(`/creneaux/${id}/cancel-booking`);
    return response.data;
  }
}

const creneauxService = new CreneauxService();
export default creneauxService;
